import { FormEvent, useEffect, useRef, useState } from 'react'
import { X, Camera, AlertCircle } from 'lucide-react'

interface BarcodeScannerProps {
  onDetected: (barcode: string) => boolean | Promise<boolean>
  onClose: () => void
  busy?: boolean
  lookupError?: string
}

export default function BarcodeScanner({ onDetected, onClose, busy, lookupError }: BarcodeScannerProps) {
  const videoRef    = useRef<HTMLVideoElement>(null)
  const canvasRef   = useRef<HTMLCanvasElement>(null)
  const streamRef   = useRef<MediaStream | null>(null)
  const detectorRef = useRef<any>(null)
  const readerRef   = useRef<any>(null)
  const rafRef      = useRef<number>(0)
  const detectedRef = useRef(false)
  const detectorFails = useRef(0)
  const startedRef = useRef(false)
  const [error, setError] = useState('')
  const [manual, setManual] = useState('')
  const [manualError, setManualError] = useState('')

  useEffect(() => {
    startCamera()
    return () => stopCamera()
  }, [])

  const stopCamera = () => {
    cancelAnimationFrame(rafRef.current)
    streamRef.current?.getTracks().forEach((track: MediaStreamTrack) => track.stop())
    try { readerRef.current?.reset?.() } catch { /* ignore */ }
    readerRef.current = null
  }

  const emitCode = async (raw: string) => {
    const code = String(raw || '').replace(/\D/g, '')
    if (code.length < 8 || detectedRef.current) return false
    detectedRef.current = true
    setManual(code)
    setManualError('')
    try {
      const ok = await onDetected(code)
      if (ok) {
        stopCamera()
        return true
      }
      await new Promise((r) => setTimeout(r, 2500))
    } catch { /* parent toast */ }
    detectedRef.current = false
    return false
  }

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Este navegador no permite cámara. Escribe el código de barras abajo.')
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
      })
      streamRef.current = stream
      if (videoRef.current) {
        const video = videoRef.current
        video.srcObject = stream
        video.setAttribute('playsinline', 'true')
        video.muted = true
        const boot = () => { void initDetector() }
        if (video.readyState >= 1) boot()
        else video.onloadedmetadata = boot
        await video.play().catch(() => {})
        if (!startedRef.current) boot()
      }
    } catch {
      setError('No se pudo acceder a la cámara. Verifica los permisos o escribe el código abajo.')
    }
  }

  const startZxing = async () => {
    if (readerRef.current || !videoRef.current) return
    detectorRef.current = null
    const { BrowserMultiFormatReader } = await import('@zxing/browser')
    const reader = new BrowserMultiFormatReader()
    readerRef.current = reader
    reader.decodeFromVideoElement(videoRef.current, (result: any) => {
      if (result) emitCode(result.getText())
    })
  }

  const initDetector = async () => {
    if (startedRef.current) return
    startedRef.current = true
    if ('BarcodeDetector' in window) {
      try {
        detectorRef.current = new (window as any).BarcodeDetector({
          formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39'],
        })
      } catch {
        try { detectorRef.current = new (window as any).BarcodeDetector() } catch { detectorRef.current = null }
      }
      if (detectorRef.current) {
        scanFrame()
        return
      }
    }
    try {
      await startZxing()
    } catch {
      setError('Tu navegador no lee la cámara. Escribe el código de barras abajo.')
    }
  }

  const scanFrame = async () => {
    if (!videoRef.current || detectedRef.current) return
    try {
      if (detectorRef.current) {
        const barcodes = await detectorRef.current.detect(videoRef.current)
        detectorFails.current = 0
        if (barcodes.length > 0 && await emitCode(barcodes[0].rawValue)) return
      }
    } catch {
      detectorFails.current += 1
      if (detectorFails.current >= 8) {
        try { await startZxing() } catch { /* manual fallback */ }
        return
      }
    }
    rafRef.current = requestAnimationFrame(scanFrame)
  }

  const captureStill = async () => {
    const video = videoRef.current
    if (!video || detectedRef.current) return
    const canvas = canvasRef.current || document.createElement('canvas')
    canvas.width = video.videoWidth || 1280
    canvas.height = video.videoHeight || 720
    canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height)
    try {
      if (detectorRef.current) {
        const barcodes = await detectorRef.current.detect(canvas)
        if (barcodes.length > 0 && await emitCode(barcodes[0].rawValue)) return
      }
      const { BrowserMultiFormatReader } = await import('@zxing/browser')
      const reader = readerRef.current || new BrowserMultiFormatReader()
      readerRef.current = reader
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9)
      const img = new Image()
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject(new Error('still'))
        img.src = dataUrl
      })
      const decodeImage = reader.decodeFromImageElement || reader.decodeFromImage
      if (typeof decodeImage === 'function') {
        const result = await decodeImage.call(reader, img)
        const text = result?.getText?.() || result?.text
        if (text && await emitCode(text)) return
      }
    } catch { /* fall through */ }
    setManualError('No se leyó en la foto. Acerca el código o escríbelo (13 dígitos).')
  }

  const submitManual = async (e: FormEvent) => {
    e.preventDefault()
    const code = manual.replace(/\D/g, '')
    if (code.length < 8) {
      setManualError('Escribe al menos 8 dígitos del código de barras')
      return
    }
    setManualError('')
    await emitCode(code)
  }

  return (
    <div className="food-plan-root food-portal-modal fixed inset-0 bg-black z-50 flex flex-col">
      <div className="flex items-center justify-between p-4 bg-black/80">
        <div className="flex items-center gap-2 text-white">
          <Camera size={20} />
          <span className="font-semibold">Escanear código de barras</span>
        </div>
        <button type="button" onClick={() => { stopCamera(); onClose() }} className="text-white p-2 hover:bg-white/10 rounded-full" aria-label="Cerrar">
          <X size={22} />
        </button>
      </div>

      <div className="flex-1 relative flex items-center justify-center bg-black">
        <video ref={videoRef} className="w-full h-full object-cover" playsInline muted autoPlay />
        <canvas ref={canvasRef} className="hidden" />

        {!error && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="relative w-[min(88vw,20rem)] h-52">
              <div className="absolute top-0 left-0 w-10 h-10 border-t-4 border-l-4 border-green-400 rounded-tl-lg" />
              <div className="absolute top-0 right-0 w-10 h-10 border-t-4 border-r-4 border-green-400 rounded-tr-lg" />
              <div className="absolute bottom-0 left-0 w-10 h-10 border-b-4 border-l-4 border-green-400 rounded-bl-lg" />
              <div className="absolute bottom-0 right-0 w-10 h-10 border-b-4 border-r-4 border-green-400 rounded-br-lg" />
              <div className="absolute inset-x-0 h-0.5 bg-green-400 animate-bounce top-1/2 opacity-80" />
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center p-6 pointer-events-none">
            <div className="bg-white rounded-2xl p-6 text-center max-w-xs pointer-events-auto">
              <AlertCircle size={40} className="text-red-400 mx-auto mb-3" />
              <p className="text-gray-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {busy && (
          <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
            <p className="text-white text-sm font-medium bg-black/70 rounded-xl px-4 py-2">Buscando en catálogo y Open Food Facts…</p>
          </div>
        )}
      </div>

      <form onSubmit={submitManual} className="p-4 bg-black/90 space-y-2">
        <p className="text-white/80 text-sm text-center">Apunta al código o escríbelo a mano (13 dígitos, sin espacios)</p>
        <div className="flex gap-2">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="Ej. 7702367292717"
            value={manual}
            disabled={busy}
            onChange={e => { setManual(e.target.value.replace(/[^\d]/g, '')); setManualError('') }}
            className="flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900"
            aria-label="Código de barras a mano"
          />
          <button
            type="submit"
            disabled={busy}
            className="rounded-xl px-4 py-2.5 text-sm font-semibold text-white bg-[#64ba30] hover:bg-[#58a529] disabled:opacity-60"
          >
            Buscar
          </button>
        </div>
        <button
          type="button"
          disabled={busy}
          onClick={captureStill}
          className="w-full rounded-xl py-2 text-sm font-medium text-white bg-white/10 hover:bg-white/15 disabled:opacity-60"
        >
          Capturar foto y leer
        </button>
        {(manualError || lookupError) && (
          <p className="text-red-300 text-xs text-center">{lookupError || manualError}</p>
        )}
        <p className="text-white/50 text-[11px] text-center">Si la tapa está curva, acerca el código o escríbelo. Cámara: Chrome/Safari en localhost o HTTPS.</p>
      </form>
    </div>
  )
}
